import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FaChevronDown, FaChevronUp, FaEdit } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Select from 'react-select';
import Navbar from '../../Navbar';
import Footer from '../../Footer';
import OutletSearch from '../../SelectSearch/OutletSearch';
import { useNavigate } from 'react-router-dom';
import { formatSalesValue } from '../../../utils/formatUtils';
import { convertToDisplayUnit } from '../../../utils/quantityConversion';
import { selectStyles } from '../../../utils/cssUtils';
import { FaTrash } from 'react-icons/fa6';

const ViewSubRecipe = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem('token');
    const navigate = useNavigate();

    const [subRecipes, setSubRecipes] = useState([]);
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const [loading, setLoading] = useState(false);
    const [expandedRows, setExpandedRows] = useState([]);
    const [showPrintMenu, setShowPrintMenu] = useState(false);

    const [selectedRecipe, setSelectedRecipe] = useState(null);

    const recipeOptions = useMemo(() => {
        return subRecipes.map(recipe => ({
            value: recipe.id,
            label: recipe.name
        }));
    }, [subRecipes]);

    const filteredRecipes = useMemo(() => {
        if (!selectedRecipe) return subRecipes;

        return subRecipes.filter(
            recipe => recipe.id === selectedRecipe.value
        );
    }, [selectedRecipe, subRecipes]);

    const fetchSubRecipes = async () => {
        if (!token || !selectedOutlet) return;

        setSelectedRecipe(null);
        setSubRecipes([]);
        setExpandedRows([]);

        setLoading(true);

        try {
            const response = await axios.get(`${baseUrl}/sub-recipes`, {
                params: {
                    token,
                    outlet: selectedOutlet
                }
            });

            setSubRecipes(response.data);
        } catch (error) {
            console.error("Error fetching sub-recipes:", error);
            toast.error("Failed to fetch sub-recipes");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token)
            fetchSubRecipes();
    }, [token, selectedOutlet, baseUrl]);

    const toggleRow = (id) => {
        const newExpandedRows = [...expandedRows];
        const index = newExpandedRows.indexOf(id);

        if (index === -1) {
            newExpandedRows.push(id);
        } else {
            newExpandedRows.splice(index, 1);
        }

        setExpandedRows(newExpandedRows);
    };

    const isRowExpanded = (id) => {
        return expandedRows.includes(id);
    };

    const handleEdit = (recipe) => {
        navigate('/subrecipecost', {
            state: {
                receivedData: recipe
            }
        })
    };

    const handleCreateSubs = () => {
        navigate('/subrecipecost');
    }

    const handleDelete = (id) => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to delete this sub-recipe?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                        onClick={async () => {
                            toast.dismiss('delete-subrecipe-confirmation');
                            setLoading(true);
                            try {
                                await axios.delete(`${baseUrl}/sub-recipe/${id}`, {
                                    headers: {
                                        'Content-Type': 'application/json'
                                    },
                                    data: {
                                        token: token,
                                        outlet: selectedOutlet
                                    }
                                });

                                setSelectedRecipe(null);
                                fetchSubRecipes();
                                toast.success('Sub-recipe deleted successfully');
                            } catch (error) {
                                console.error('Error deleting sub-recipe:', error);
                                toast.error('Failed to delete sub-recipe');
                            } finally {
                                setLoading(false);
                            }
                        }}
                    >
                        Delete
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss('delete-subrecipe-confirmation')}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'delete-subrecipe-confirmation'
            }
        );
    };

    const generatePrintHTML = (withIngredients) => {
        const rows = filteredRecipes.map((recipe) => {
            const mainRow = `
                <tr>
                    <td>${recipe.name}</td>
                    <td>${recipe.unit || '-'}</td>
                    <td>${recipe.uom || '-'}</td>
                    <td>${formatSalesValue(recipe.costprice)}</td>
                </tr>
            `;

            if (!withIngredients) return mainRow;

            const hasItems = recipe.items?.length > 0;

            if (!hasItems) {
                return `
                    ${mainRow}
                    <tr>
                        <td colspan="4" style="background:#f9fafb; padding: 8px 12px;">
                            <p style="color:#6b7280; margin: 4px 0;">No ingredients for this item</p>
                        </td>
                    </tr>
                `;
            }

            return `
                ${mainRow}
                <tr>
                    <td colspan="4" style="background:#f9fafb; padding: 8px 12px;">
                        <div style="font-weight:600; margin-bottom:4px;">Ingredients</div>
                        <table class="sub-table">
                            <thead><tr><th>Ingredient</th><th>Qty</th><th>UOM</th><th>Cost</th><th>Unit</th><th>Rate</th></tr></thead>
                            <tbody>
                                ${recipe.items.map((item) => `
                                    <tr>
                                        <td>${item.name}</td>
                                        <td>${convertToDisplayUnit(item.quantity, item.uom, item.new_uom, item.unit)}</td>
                                        <td>${item.new_uom || item.uom}</td>
                                        <td>${formatSalesValue(item.cost)}</td>
                                        <td>${item.unit} (${item.uom})</td>
                                        <td>${formatSalesValue(item.rate)}</td>
                                    </tr>
                                `).join("")}
                            </tbody>
                        </table>
                    </td>
                </tr>
            `;
        }).join("");

        return `
            <html>
            <head>
                <title>Sub-Recipe Print - ${selectedOutlet || ""}</title>
                <style>
                    @page {
                        size: auto;
                        margin: 5mm 5mm;
                    }
                    * { box-sizing: border-box; }
                    html, body { margin: 0; }
                    body { font-family: Arial, sans-serif; padding: 4px; color: #111827; }
                    h2 { margin: 0 0 12px; font-size: 16px; }
                    table.main-table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-bottom: 16px;
                        outline: 1px solid #d1d5db;
                    }
                    table.main-table th, table.main-table td {
                        border: 1px solid #d1d5db;
                        padding: 6px 10px;
                        text-align: center;
                        font-size: 13px;
                    }
                    table.main-table th { background: #f3f4f6; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    table.sub-table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-bottom: 8px;
                        outline: 1px solid #e5e7eb;
                    }
                    table.sub-table th, table.sub-table td {
                        border: 1px solid #e5e7eb;
                        padding: 4px 8px;
                        font-size: 12px;
                        text-align: center;
                    }
                    table.sub-table th { background: #f9fafb; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    @media print {
                        body { padding: 4px; }
                    }
                </style>
            </head>
            <body>
                <h2>Sub-Recipes${selectedOutlet ? ` - ${selectedOutlet}` : ""}</h2>
                <table class="main-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Unit</th>
                            <th>UOM</th>
                            <th>Cost</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${rows}
                    </tbody>
                </table>
            </body>
            </html>
        `;
    };

    const handlePrint = (withIngredients) => {
        setShowPrintMenu(false);

        if (!filteredRecipes || filteredRecipes.length === 0) {
            toast.error("No sub-recipes available to print");
            return;
        }

        const printWindow = window.open("", "_blank");
        if (!printWindow) {
            toast.error("Unable to open print window. Please allow pop-ups for this site.");
            return;
        }

        printWindow.document.write(generatePrintHTML(withIngredients));
        printWindow.document.close();

        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
        };
    };

    return (
        <div className='min-h-screen flex flex-col'>
            <Navbar />

            <div className='flex-grow'>
                <div className="max-w-5xl mx-auto my-3">
                    <div className="flex justify-center mb-2.5">
                        <OutletSearch
                            selectedOutlet={selectedOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                            token={token}
                            compact={true}
                        />
                    </div>

                    <div className='flex justify-between items-center mb-3'>
                        <div className="max-w-md w-full">
                            <Select
                                options={recipeOptions}
                                value={selectedRecipe}
                                onChange={setSelectedRecipe}
                                placeholder="Search sub-recipe..."
                                isClearable
                                className="text-left z-30"
                                styles={selectStyles}
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <div className="relative">
                                <button
                                    onClick={() => setShowPrintMenu((prev) => !prev)}
                                    className="px-3 py-1.5 rounded-md text-sm font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 hover:shadow-md whitespace-nowrap"
                                >
                                    Print Menu
                                </button>

                                {showPrintMenu && (
                                    <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-40">
                                        <button
                                            onClick={() => handlePrint(true)}
                                            className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                        >
                                            With Ingredients
                                        </button>
                                        <button
                                            onClick={() => handlePrint(false)}
                                            className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                        >
                                            Without Ingredients
                                        </button>
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={handleCreateSubs}
                                className="px-3 py-1.5 rounded-md text-sm font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md whitespace-nowrap"
                            >
                                Create Sub-recipe
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
                        </div>
                    ) : (
                        <div className="rounded-md border overflow-x-auto mb-8">
                            <table className="min-w-full divide-y divide-gray-200 text-center">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th></th>
                                        <th className="px-3 py-2.5 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th className="px-3 py-2.5 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                            Unit
                                        </th>
                                        <th className="px-3 py-2.5 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                            UOM
                                        </th>
                                        <th className="px-3 py-2.5 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                            Cost
                                        </th>
                                        <th className="px-3 py-2.5 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredRecipes.map((recipe) => {
                                        const isExpanded = isRowExpanded(recipe.id);

                                        return (
                                            <>
                                                <tr
                                                    key={recipe.id}
                                                    className="hover:bg-gray-50"
                                                >
                                                    <td
                                                        className="px-3 py-2 text-base text-gray-600 cursor-pointer"
                                                        onClick={() => toggleRow(recipe.id)}
                                                    >
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleRow(recipe.id);
                                                            }}
                                                            className="text-gray-600 hover:text-gray-700"
                                                        >
                                                            {isExpanded ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
                                                        </button>
                                                    </td>
                                                    <td
                                                        className="px-3 py-2.5 whitespace-nowrap text-sm font-medium text-gray-900 cursor-pointer"
                                                        onClick={() => toggleRow(recipe.id)}
                                                    >
                                                        {recipe.name}
                                                    </td>
                                                    <td
                                                        className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-600 cursor-pointer"
                                                        onClick={() => toggleRow(recipe.id)}
                                                    >
                                                        {recipe.unit || '-'}
                                                    </td>
                                                    <td
                                                        className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-600 cursor-pointer"
                                                        onClick={() => toggleRow(recipe.id)}
                                                    >
                                                        {recipe.uom || '-'}
                                                    </td>
                                                    <td
                                                        className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-900 cursor-pointer"
                                                        onClick={() => toggleRow(recipe.id)}
                                                    >
                                                        {formatSalesValue(recipe.costprice)}
                                                    </td>
                                                    <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-600">
                                                        <div className="flex justify-center space-x-2">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEdit(recipe);
                                                                }}
                                                                className="p-1.5 rounded border border-gray-300 text-blue-600 hover:bg-blue-50 transition-colors"
                                                            >
                                                                <FaEdit className="h-3 w-3" />
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDelete(recipe.id);
                                                                }}
                                                                className="p-1.5 rounded border border-gray-300 text-red-600 hover:bg-red-50 transition-colors"
                                                            >
                                                                <FaTrash className="h-3 w-3" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr>
                                                        <td colSpan={6} className="p-3">
                                                            <div className="rounded-md border overflow-x-auto">
                                                                <table className="min-w-full divide-y divide-gray-200 text-center">
                                                                    <thead className="bg-gray-50">
                                                                        <tr>
                                                                            <th className="px-3 py-2.5 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                                                Ingredient
                                                                            </th>
                                                                            <th className="px-3 py-2.5 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                                                Quantity
                                                                            </th>
                                                                            <th className="px-3 py-2.5 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                                                UOM
                                                                            </th>
                                                                            <th className="px-3 py-2.5 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                                                Cost
                                                                            </th>
                                                                            <th className="px-3 py-2.5 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                                                Unit
                                                                            </th>
                                                                            <th className="px-3 py-2.5 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                                                Rate
                                                                            </th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                                        {recipe.items.map((item, index) => (
                                                                            <tr key={index}>
                                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-900">
                                                                                    {item.name}
                                                                                </td>
                                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-600">
                                                                                    {convertToDisplayUnit(
                                                                                        item.quantity,
                                                                                        item.uom,
                                                                                        item.new_uom,
                                                                                        item.unit
                                                                                    )}
                                                                                </td>
                                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-600">
                                                                                    {item.new_uom || item.uom}
                                                                                </td>
                                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-900">
                                                                                    {formatSalesValue(item.cost)}
                                                                                </td>
                                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-600">
                                                                                    {item.unit} ({item.uom})
                                                                                </td>
                                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-600">
                                                                                    {formatSalesValue(item.rate)}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {!loading && subRecipes.length === 0 && (
                        <div className="text-center text-gray-600 py-4">No sub-recipes found</div>
                    )}
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default ViewSubRecipe;